/**
 * @file 404 页
 * @description 未知路由兜底；左侧弹弓循环弹走「404」，右侧文案与返回首页。
 *
 * @whenToUse 由路由 `*` 使用。
 * @whenNotToUse 业务「空数据」态应使用列表/表格空态，而不是本页。
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button.tsx'
import { SlingshotScene } from '@/components/pages/not-found-page/slingshot-scene.tsx'

export function NotFoundPage() {
  const navigate = useNavigate()
  const [exitNonce, setExitNonce] = useState(0)
  const [exiting, setExiting] = useState(false)

  /**
   * 点击返回：打断循环并触发强力弹射，闪光结束后跳转首页。
   */
  function handleGoHome() {
    if (exiting) return
    setExiting(true)
    setExitNonce((n) => n + 1)
  }

  return (
    <div className="relative flex min-h-full overflow-hidden bg-linear-to-br from-canvas via-surface to-border text-foreground">
      {/* 微弱噪点纹理（SVG feTurbulence，不引入业务 CSS） */}
      <svg
        className="pointer-events-none absolute inset-0 size-full opacity-5"
        aria-hidden
      >
        <filter id="not-found-noise">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.85"
            numOctaves="4"
            stitchTiles="stitch"
          />
        </filter>
        <rect width="100%" height="100%" filter="url(#not-found-noise)" />
      </svg>

      <div className="relative z-10 mx-auto grid min-h-full w-full max-w-7xl grid-cols-1 items-center gap-8 px-6 py-10 lg:grid-cols-2 lg:gap-4 lg:px-10">
        <SlingshotScene
          exitNonce={exitNonce}
          onExitComplete={() => navigate('/')}
          className="order-2 min-h-96 lg:order-1 lg:min-h-screen"
        />

        <div className="order-1 flex flex-col items-start gap-5 lg:order-2 lg:pl-8">
          <p className="text-xs font-medium tracking-wide text-muted uppercase">
            404
          </p>
          <h1 className="max-w-md text-2xl font-semibold text-muted md:text-3xl">
            Oops！页面溜走了～
          </h1>
          <p className="max-w-sm text-sm text-muted">
            这个地址没有对应页面。可以返回首页继续浏览，或检查一下链接是否写对。
          </p>
          <Button
            variant="primary"
            disabled={exiting}
            onClick={handleGoHome}
            className="transition-[opacity,transform] duration-200 hover:-translate-y-0.5"
          >
            返回首页
          </Button>
        </div>
      </div>
    </div>
  )
}
